import React, { Component } from 'react';
import { connect } from 'react-redux';
import { has, isEqual } from 'lodash';
import { GroupCellRenderer } from 'components/GroupCellRenderer';
import { DroppableCellRenderer } from 'components/shared/DroppableCellRenderer';
import ExchangeRateCellRenderer from './ExchangeRateCellRenderer';
import { Grid } from 'components/shared/Grid';
import {
  ADD_GROUP,
  ASSIGN_ACCOUNT,
  ASSIGN_ACCOUNTS,
  EDIT_ACCOUNT_DESCRIPTION,
  EDIT_GROUP_CODE,
  EDIT_GROUP_NUMBER,
  MOVE_GROUP,
  MOVE_GROUP_DOWN,
  MOVE_GROUP_UP,
  REMOVE_GROUP,
  RENAME_GROUP,
  SET_GROUPS_EXPANDED,
  SET_SHOULD_SYNC_EXPANDED_STATES,
  UNASSIGN_ACCOUNT,
  SET_COLUMN_STATE,
  SET_GROUP_STATE,
  SORT_GROUPS,
  SORT_ACCOUNTS,
  LEAD_SCHEDULE_REPORT_SAVE_SELECTED_GROUPS,
} from 'constants/actionTypes';
import groupRowsSelector from 'selectors/groupRows';
import { groupIdMaxSelector } from 'selectors/groups';
import { periodColumnsSelector, periodIdsSelector } from 'selectors/periods';
import { gridStateSelector } from 'selectors/gridState';
import { settingsSelector } from 'selectors/settings';
import {
  isDescendantOf,
  isRowCollapsed,
  recursivelyCollapse,
} from 'utils/grid';
import {
  gridComponentDidUpdate,
  handleGridChange,
  handleGridReady,
} from 'utils/grid';
import { getSubgroupsData } from 'utils/leadSchedule';
import { GROUPS_PATH } from 'constants/paths';
import { GROUPS_GRID, SortingOrder, CellIds } from 'constants/grid';
import {
  exportLeadScheduleReportWithMultipleGroups,
  getSelectedGroupsIds,
} from 'utils/leadSchedule';
import { exportCellFormatter, copyCellFormatter } from 'utils/generic';

class GroupsGrid extends Component {
  constructor(props) {
    super(props);

    this.gridRef = React.createRef();
    this.frameworkComponents = {
      groupCellRenderer: (params) => (
        <DroppableCellRenderer {...params} onDrop={props.assignAccount}>
          <GroupCellRenderer {...params} />
        </DroppableCellRenderer>
      ),
      droppableCellRenderer: (params) => (
        <DroppableCellRenderer {...params} onDrop={props.assignAccount} />
      ),
      exchangeRateCellRenderer: (params) => (
        <ExchangeRateCellRenderer {...params} />
      ),
    };

    this.state = {
      groupsSortByField: 'code',
      groupsSortingOrder: SortingOrder.DESC,
      accountsSortByField: '',
      accountsSortingOrder: SortingOrder.DESC,
    };

    this.defaultColDef = {
      sortable: false,
      cellRenderer: 'droppableCellRenderer',
    };
  }

  autoGroupColumnDef = {
    headerName: 'Groups',
    field: 'description',
    colId: 'description',
    rowDrag: true,
    sortable: false,
    width: 300,
    editable: (params) =>
      params.data.cgroupId === undefined || params.data.cgroupId === null,
    comparator: (valueA, valueB) => {
      let nA = parseInt(valueA.split('-')[0], 10);
      let nB = parseInt(valueB.split('-')[0], 10);
      return Math.sign(nA - nB);
    },
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      innerRenderer: 'groupCellRenderer',
    },
  };
  componentDidUpdate(prevProps) {
    // mostly to cover the case of user loading a client file.
    // Can't use groupIdsSelector because it updates whenever a group is updated,
    // and since group is updated when its expanded flag is changed, it would
    // cause circular update
    // TODO: Use 'shouldSyncExpandedStates' in any method which changes 'groupIdMax'
    const isGroupIdMaxChanged = prevProps.groupIdMax !== this.props.groupIdMax;
    const shouldSyncExpandedStates =
      this.props.shouldSyncExpandedStates &&
      !prevProps.shouldSyncExpandedStates;
    if (isGroupIdMaxChanged || shouldSyncExpandedStates) {
      this.syncExpandStates();
    }

    const {
      periodIds,
      colState,
      setColumnState,
      setGroupState,
      path,
    } = this.props;

    const havePeriodsChanged = !isEqual(prevProps.periodIds, periodIds);
    if (havePeriodsChanged) {
      gridComponentDidUpdate(
        colState,
        setColumnState,
        setGroupState,
        path,
        GROUPS_GRID,
        this.columnApi
      );
    }
  }

  componentDidMount() {
    this.gridRef.current.addEventListener(
      'keydown',
      this.preventUnwantedKeyboardShortcuts
    );
  }

  componentWillUnmount() {
    this.gridRef.current.removeEventListener(
      'keydown',
      this.preventUnwantedKeyboardShortcuts
    );
  }

  // Prevent unwanted keyboard shortcut effects, such as vertical scroll in grid
  preventUnwantedKeyboardShortcuts = (e) => {
    if (!e.ctrlKey) return;

    const shouldSuppressEvent = this.shouldSuppressKeyboardEvent(e);
    if (shouldSuppressEvent) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  handleGridReady = ({ api, columnApi }) => {
    const { colState, path } = this.props;

    handleGridReady(path, colState, GROUPS_GRID, api, columnApi);

    this.columnApi = columnApi;
    this.gridApi = api;
    this.syncExpandStates();
  };

  handleColumnResized = ({ columnApi, finished, source }) => {
    if (source === 'uiColumnDragged' && finished) {
      const { colState, setColumnState, setGroupState, path } = this.props;
      handleGridChange(
        path,
        colState,
        setColumnState,
        setGroupState,
        GROUPS_GRID,
        columnApi
      );
    }
  };

  handleColumnVisible = ({ columnApi }) => {
    const { colState, setColumnState, setGroupState, path } = this.props;
    handleGridChange(
      path,
      colState,
      setColumnState,
      setGroupState,
      GROUPS_GRID,
      columnApi
    );
  };

  /**
   * Iterate over all grid nodes and ensure their expanded state reflect the state from redux.
   * onGroupExpandedOrCollapsed() is called at the end to "refresh" grid if any change was made
   */
  syncExpandStates = () => {
    this.props.setShouldSyncExpandedStates(false);
    let needsUpdate = false;
    this.gridApi.forEachNodeAfterFilter((n) => {
      if (n.data.expanded !== undefined && n.data.expanded !== n.expanded) {
        n.expanded = n.data.expanded;
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      this.gridApi.onGroupExpandedOrCollapsed();
    }
  };

  handleRowGroupOpened = ({ node, api }) => {
    const updatedGroupIds = [node.data.id];
    // when a group is collapsed, make sure you also collapse all of its children
    if (!node.expanded) {
      const affectedRows = recursivelyCollapse(node);
      updatedGroupIds.push(...affectedRows.map((n) => n.data.id));
      // Calling node.setExpanded() causes the grid to get redrawn, so it is best to set
      // node.expanded=false directly, and then call api.onGroupExpandedOrCollapsed() when finished
      // to get the grid to redraw the grid again just once.
      api.onGroupExpandedOrCollapsed();
    }

    this.props.setGroupsExpanded(updatedGroupIds, node.expanded);
  };

  handleCellValueChanged = (event) => {
    if (event.data.group) {
      if (event.colDef.field === 'number') {
        this.props.editGroupNumber(event.data.id, event.data.number);
      } else if (event.colDef.field === 'code') {
        this.props.editGroupCode(event.data.id, event.data.code);
      } else {
        this.props.renameGroup(event.data.id, event.data.description);
      }
    } else {
      this.props.editAccountDescription(event.data.id, event.data.description);
    }
  };

  handleRowDragEnd = (event) => {
    if (!event.overNode || !event.overNode.data.group) return;
    let targetGroupId = event.overNode.data.id;

    if (event.node.data.group) {
      let groupId = event.node.data.id;
      this.props.moveGroup(groupId, targetGroupId);
    } else {
      const accountId = event.node.data.id;
      const selectedAccountIds = event.api
        .getSelectedRows()
        .map((row) => row.id);
      const isDraggedAccountInSelectedAccounts = selectedAccountIds.includes(
        accountId
      );

      if (!isDraggedAccountInSelectedAccounts) {
        this.props.assignAccount(accountId, targetGroupId);
      } else {
        this.props.assignAccounts(selectedAccountIds, targetGroupId);
      }
    }
  };

  getColFields = (columnsToHide = []) => {
    const { cols } = this.props;
    const fields = [];

    cols.forEach((col) => {
      if (has(col, 'groupId') && !col.groupId.startsWith('period')) {
        return;
      }

      if (has(col, 'field')) {
        fields.push({
          headerName: col.headerName,
          field: col.field,
        });
      }
      if (has(col, 'children')) {
        col.children.forEach((child) => {
          fields.push({
            headerName: `${child.headerName} - ${col.headerName}`,
            field: child.field,
          });
        });
      }
    });

    const filteredFields = fields.filter((colField) => {
      const [columnLabel] = colField.field.split('-');
      return !columnsToHide.includes(columnLabel);
    });

    return filteredFields;
  };

  getGroupsSortOrderSubMenu = (groupId, field) => {
    const { rows, cols } = this.props;
    const sortingOrders = Object.values(SortingOrder);
    return sortingOrders.map((sortingOrder) => ({
      name: sortingOrder,
      action: () =>
        this.props.sortGroups(groupId, field, sortingOrder, rows, cols),
    }));
  };

  getGroupsSortSubMenu = (groupId) => {
    if (groupId === null) {
      return [];
    }

    const colFields = this.getColFields();
    return colFields.map((colField) => ({
      name: `By ${colField.headerName}`,
      subMenu: this.getGroupsSortOrderSubMenu(groupId, colField.field),
    }));
  };

  processCellForClipboard = (params) => {
    const { settings } = this.props;

    const cellFormatter = copyCellFormatter(settings);

    return cellFormatter(params);
  };

  getContextMenuItems = (params) => {
    const {
      colState,
      setColumnState,
      setGroupState,
      path,
      savedGroupsIds,
      settings,
    } = this.props;

    const groupsExportDef = {
      columnGroups: true,
      fileName: 'Groups listing report',
      shouldRowBeSkipped: ({ node }) => isRowCollapsed(node),
      processCellCallback: (params) => {
        const { value, node, column } = params;
        if (column.colId === 'ag-Grid-AutoColumn') {
          const indent = '   '.repeat(node.level);
          return `${indent}${value}`;
        }

        const cellFormatter = exportCellFormatter(settings);

        return cellFormatter(params);
      },
    };

    const hasSavedGroupsIds = !!savedGroupsIds;

    const selectedGroupsIds = getSelectedGroupsIds(params);
    const hasSelectedGroups = selectedGroupsIds && selectedGroupsIds.length;

    const groupActions = [
      {
        name: 'Group options',
        disabled: !params.node,
        subMenu: [
          {
            name: 'Move Up',
            shortcut: 'Ctrl + Up',
            action: () =>
              this.props.moveGroupUp(params.node ? params.node.data.id : 0),
          },
          {
            name: 'Move Down',
            shortcut: 'Ctrl + Down',
            action: () =>
              this.props.moveGroupDown(params.node ? params.node.data.id : 0),
          },
        ],
      },
      {
        name: 'Sort groups',
        disabled: !params.node,
        subMenu: this.getGroupsSortSubMenu(
          params.node && params.node.data ? params.node.data.id : null
        ),
      },
      'separator',
      {
        name: `New ${params.node ? 'Subgroup' : 'Group'}`,
        action: () =>
          this.props.addGroup(params.node ? params.node.data.id : 0),
      },
      {
        name: 'Remove Selected Groups',
        disabled: !params.node || !hasSelectedGroups,
        action: () => {
          params.api.getSelectedNodes().forEach((node) => {
            if (node.data.group) {
              this.props.removeGroup(node.data.id);
            }
          });
        },
      },
      {
        name: 'Copy',
        disabled: !params.node,
        shortcut: 'Ctrl + C',
        action: () => {
          params.api.copySelectedRangeToClipboard();
        },
      },
      'separator',
      'expandAll',
      'contractAll',
      'separator',
      {
        name: 'Fit All Columns',
        action: () => {
          params.api.sizeColumnsToFit();
          handleGridChange(
            path,
            colState,
            setColumnState,
            setGroupState,
            GROUPS_GRID,
            params.columnApi
          );
        },
      },
      'separator',
      {
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => {
              params.api.exportDataAsCsv(groupsExportDef);
            },
          },
          {
            name: 'Excel Export',
            action: () => {
              params.api.exportDataAsExcel({
                ...groupsExportDef,
                exportMode: 'xml',
              });
            },
          },
          {
            name: 'Lead schedule report',
            disabled: !params.node,
            subMenu: [
              {
                name: 'Export saved LS groups',
                disabled: !hasSavedGroupsIds,
                action: () =>
                  exportLeadScheduleReportWithMultipleGroups(
                    params,
                    settings,
                    savedGroupsIds
                  ),
              },
              {
                name: 'Export current LS group',
                action: () => {
                  params.api.exportDataAsExcel({
                    ...groupsExportDef,
                    exportMode: 'xml',
                    fileName: 'Lead schedule report',
                    sheetName: 'Lead schedule report',
                    customHeader: getSubgroupsData(params, settings),
                    shouldRowBeSkipped: ({ node }) =>
                      !isDescendantOf(params.node, node),
                  });
                },
              },
              'separator',
              {
                name: 'Show previously saved groups',
                disabled: !hasSavedGroupsIds,
                action: () => {
                  params.api.deselectAll();
                  params.api.forEachNode((node) => {
                    const isGroup = !!node.data.group;
                    const isSelected =
                      isGroup && savedGroupsIds.includes(node.data.id);
                    if (isSelected) {
                      // set selected, keep any other selections
                      node.setSelected(true);
                    }
                  });
                },
              },
              {
                name: 'Save selected groups',
                disabled: !hasSelectedGroups,
                action: () => {
                  const selectedGroupsIds = getSelectedGroupsIds(params);
                  this.props.leadScheduleReportSaveSelectedGroups(
                    selectedGroupsIds
                  );
                },
              },
            ],
          },
        ],
      },
    ];

    const accountActions = [
      {
        name: 'Unassign Account',
        action: () => this.props.unassignAccount(params.node.data.id),
      },
    ];

    return !params.node || params.node.data.group
      ? groupActions
      : accountActions;
  };

  shouldSuppressKeyboardEvent = (event) => {
    const ctrlKeysToSuppress = ['ArrowUp', 'ArrowDown', 'c'];

    if (event.ctrlKey) {
      return ctrlKeysToSuppress.includes(event.key);
    }
  };

  onCellKeyDown = (e) => {
    if (!e.node || !e.node.data.group) {
      return;
    }
    const { event, node, rowIndex, api } = e;

    const ctrlKeysActions = {
      ArrowUp: () => this.props.moveGroupUp(node ? node.data.id : 0),
      ArrowDown: () => this.props.moveGroupDown(node ? node.data.id : 0),
      c: () => {
        api.copySelectedRangeToClipboard();
      },
    };

    if (event.ctrlKey && event.key in ctrlKeysActions) {
      e.api.setFocusedCell(rowIndex, 'description', null);
      ctrlKeysActions[event.key]();
    }
  };

  render() {
    return (
      <div className="groups">
        <div ref={this.gridRef} className="groups-grid">
          <Grid
            columnDefs={this.props.cols}
            rowData={this.props.rows}
            autoGroupColumnDef={this.autoGroupColumnDef}
            frameworkComponents={this.frameworkComponents}
            defaultColDef={this.defaultColDef}
            getContextMenuItems={this.getContextMenuItems}
            animateRows={true}
            treeData={true}
            enableMultiRowDragging={true}
            rowSelection="multiple"
            rowDeselection={true}
            getDataPath={(data) => data.groupPath}
            getRowNodeId={(data) => `${data.group ? 'G' : 'A'}-${data.id}`}
            onGridReady={this.handleGridReady}
            onRowGroupOpened={this.handleRowGroupOpened}
            onCellKeyDown={this.onCellKeyDown}
            suppressKeyboardEvent={({ event }) =>
              this.shouldSuppressKeyboardEvent(event)
            }
            enableRangeSelection={true}
            processCellForClipboard={this.processCellForClipboard}
            onCellValueChanged={this.handleCellValueChanged}
            onRowDragEnd={this.handleRowDragEnd}
            onColumnVisible={this.handleColumnVisible}
            onColumnResized={this.handleColumnResized}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    cols: [
      {
        field: 'number',
        colId: 'number',
        headerName: 'Number',
        editable: true,
        width: 80,
        cellClass: CellIds.string,
      },
      {
        field: 'code',
        colId: 'code',
        headerName: 'Code',
        editable: true,
        width: 80,
        cellClass: CellIds.string,
      },
      {
        field: 'exchangeRate',
        colId: 'exchangeRate',
        headerName: 'Exchange Rate',
        editable: true,
        width: 80,
        cellRenderer: 'exchangeRateCellRenderer',
      },
      ...periodColumnsSelector(state, {
        isGroups: true,
        useGroupsPrecision: true,
        editable: true,
      }),
    ],
    periodIds: periodIdsSelector(state),
    rows: groupRowsSelector(state),
    groupIdMax: groupIdMaxSelector(state),
    colState: gridStateSelector(state),
    savedGroupsIds: state.groups.savedGroupsIds,
    shouldSyncExpandedStates: state.groups.shouldSyncExpandedStates,
    settings: settingsSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  renameGroup: (groupId, newName) =>
    dispatch({ type: RENAME_GROUP, groupId, newName }),
  editGroupCode: (groupId, groupCode) =>
    dispatch({ type: EDIT_GROUP_CODE, groupId, groupCode }),
  editGroupNumber: (groupId, groupNumber) =>
    dispatch({ type: EDIT_GROUP_NUMBER, groupId, groupNumber }),
  addGroup: (parentGroupId) => dispatch({ type: ADD_GROUP, parentGroupId }),
  removeGroup: (groupId) => dispatch({ type: REMOVE_GROUP, groupId }),
  moveGroup: (groupId, targetGroupId) =>
    dispatch({ type: MOVE_GROUP, groupId, targetGroupId }),
  moveGroupUp: (groupId) => dispatch({ type: MOVE_GROUP_UP, groupId }),
  moveGroupDown: (groupId) => dispatch({ type: MOVE_GROUP_DOWN, groupId }),
  setGroupsExpanded: (groupIds, expanded) =>
    dispatch({ type: SET_GROUPS_EXPANDED, groupIds, expanded }),
  setShouldSyncExpandedStates: (shouldSyncExpandedStates) =>
    dispatch({
      type: SET_SHOULD_SYNC_EXPANDED_STATES,
      shouldSyncExpandedStates,
    }),
  leadScheduleReportSaveSelectedGroups: (savedGroupsIds) =>
    dispatch({
      type: LEAD_SCHEDULE_REPORT_SAVE_SELECTED_GROUPS,
      savedGroupsIds,
    }),
  editAccountDescription: (accountId, newDescription) =>
    dispatch({ type: EDIT_ACCOUNT_DESCRIPTION, accountId, newDescription }),
  assignAccount: (accountId, groupId) =>
    dispatch({ type: ASSIGN_ACCOUNT, accountId, groupId }),
  assignAccounts: (accountIds, groupId) =>
    dispatch({ type: ASSIGN_ACCOUNTS, accountIds, groupId }),
  unassignAccount: (accountId) =>
    dispatch({ type: UNASSIGN_ACCOUNT, accountId }),
  setColumnState: (path, columnState) =>
    dispatch({
      type: SET_COLUMN_STATE,
      path,
      grid: GROUPS_GRID,
      columnState,
    }),
  setGroupState: (path, groupState) =>
    dispatch({
      type: SET_GROUP_STATE,
      path,
      grid: GROUPS_GRID,
      groupState,
    }),
  sortGroups: (groupId, field, sortingOrder, rows, cols) =>
    dispatch({ type: SORT_GROUPS, groupId, field, sortingOrder, rows, cols }),
  sortAccounts: (field, rows, sortingOrder) =>
    dispatch({ type: SORT_ACCOUNTS, field, rows, sortingOrder }),
});

GroupsGrid.defaultProps = {
  path: GROUPS_PATH,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupsGrid);
